import * as yup from 'yup'

import type { AppointmentInputType } from 'types/appointments'
import type { ProviderType } from 'types/providers'
import type { Certification } from 'utils/enums'

import { ApptFields } from 'components/Appointments/utils'
import i18next from 'i18n'

export type RequiredProviderType = {
  certification: Certification
  count: number
  specialtyValue?: string
}

export const createAppointmentInputSchema = (
  requiredProviderTypes: RequiredProviderType[],
  providers: ProviderType[],
): yup.Schema<AppointmentInputType> => {
  const providersById = providers.reduce<Record<string, ProviderType>>(
    (acc, p) => {
      acc[p.id] = p
      return acc
    },
    {},
  )

  return yup.object().shape({
    [ApptFields.TREATMENT_PROCEDURE_ID]: yup.string().required(),
    [ApptFields.START_TIME]: yup
      .string()
      .required(i18next.t('Select a timeslot on the calendar to set a date.')),
    [ApptFields.END_TIME]: yup.string().required(),
    [ApptFields.DURATION_BEFORE]: yup.string().required(),
    [ApptFields.ROOM_ID]: yup
      .string()
      .required(i18next.t('Please select a room.')),
    [ApptFields.PROVIDER_IDS]: yup
      .array(yup.string().required())
      .required(i18next.t('At least one provider is required.'))
      .test(
        'required-provider-types',
        i18next.t('Please select all required providers.'),
        function(value) {
          const ids = value || []
          for (const {
            certification,
            count,
            specialtyValue,
          } of requiredProviderTypes) {
            const selectedCount = ids.filter(id => {
              const provider = providersById[id]
              if (!provider) return false
              if (provider.certification !== certification) return false

              // If no specialty required any provider with this certification counts
              if (!specialtyValue) return true

              // For specialty requirements provider must have that specialty value
              return provider.providerSpecialties?.some(
                s => s.value === specialtyValue,
              )
            }).length
            if (selectedCount < count) {
              return this.createError({
                path: this.path,
                message: i18next.t('validationAppointment.requiredProviders', {
                  count,
                  certification: i18next.t(certification),
                  specialtyText: specialtyValue
                    ? i18next.t('validationAppointment.forSpecialty', {
                        specialty: i18next.t(specialtyValue),
                      })
                    : '',
                }),
              })
            }
          }
          return true
        },
      ),
  })
}
